import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import {
  getAssetRelations,
  getAssetTypeIcon,
  getAssetTypeName,
  getRelationTypeName,
  useOpenAsset,
} from '../../../data/assets/helpers';
import { TpAssetInfo, TpAssetRelationInfo, TpCollapsedRelationSet } from '../../../data/assets/interfaces';
import { useAppDispatch, useAppSelector, useUser } from '../../../app/hooks';
import {
  addNewAsset,
  modifyAsset,
  modifyOpenedAsset,
  selectQueriedAssets,
} from '../../../data/assets/queriedAssetsSlice';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import styles from '../AssetView.module.scss';
import { ModalPortal } from '../../../components/modal-portal/ModalPortal';
import { promptAddRelationWizard, promptDeleteRelation } from '../../crud-asset/CrudAsset';
import { useApiContext } from '../../../api';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import RelationLink from './RelationLink';

function collapseRelations(relations: TpAssetRelationInfo[]): TpCollapsedRelationSet[] {
  // Takes a list of relations, and returns a list of (potentially) collapsed relations,
  // with relations types that contain more than a specified number of instances are grouped
  const thresholdCount = 5;
  const totalCount = relations.length;
  const relationsTypesMap: { [type: string]: TpCollapsedRelationSet } = {};
  for (const rel of relations) {
    const uniqueRelTypeKey = `${rel.type}_${rel.toAsset.type}_${rel.isReverse}`;
    if (!relationsTypesMap[uniqueRelTypeKey])
      relationsTypesMap[uniqueRelTypeKey] = {
        type: rel.type,
        isReverse: rel.isReverse,
        toAssetType: rel.toAsset.type,
        relations: [],
      };
    relationsTypesMap[uniqueRelTypeKey].relations.push(rel);
  }
  const collapsedRelations: TpCollapsedRelationSet[] = [];
  for (const relTypeId in relationsTypesMap) {
    const relType = relationsTypesMap[relTypeId];
    if (relType.relations.length >= thresholdCount || totalCount >= thresholdCount) {
      collapsedRelations.push(relType);
    }
  }
  for (const rel of relations) {
    const uniqueRelTypeKey = `${rel.type}_${rel.toAsset.type}_${rel.isReverse}`;
    if (relationsTypesMap[uniqueRelTypeKey].relations.length < thresholdCount && totalCount < thresholdCount)
      collapsedRelations.push({
        type: rel.type,
        isReverse: rel.isReverse,
        toAssetType: rel.toAsset.type,
        relations: [rel],
      });
  }
  return collapsedRelations;
}

interface TpRelationProps {
  assetInfo: TpAssetInfo;
  children?: React.ReactNode;
  dashboardtemplate?: any;
  relations: TpAssetRelationInfo[];
  setRelations: (relations: TpAssetRelationInfo[]) => void;
}

const RelatedAssetsPopup = ({ relatedAssetInfo }: { relatedAssetInfo: TpCollapsedRelationSet }) => {
  // Displays a popup allowing the user to pick a specific related asset in a collapsed set of relations
  const openAsset = useOpenAsset();
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  return (
    <div style={{ padding: '30px', width: '70vw' }}>
      {relatedAssetInfo.relations.map((rel) => (
        <div
          key={rel.toAsset.id}
          style={{ position: 'relative', display: 'inline-block', margin: '7px' }}
          className={styles.relatedAsset}
          onClick={() => {
            openAsset(rel.toAsset.id);
          }}
        >
          <div className={styles.relatedAssetIcon}>
            <FontAwesomeIcon icon={['fas', getAssetTypeIcon(ontology.assetTypes[rel.toAsset.type])]} />
          </div>
          <div className={styles.relatedAssetTypeName}>{getAssetTypeName(ontology.assetTypes[rel.toAsset.type])}</div>
          <div className={styles.relatedAssetName}>{rel.toAsset.displayName}</div>
        </div>
      ))}
    </div>
  );
};

const Relations = (props: TpRelationProps) => {
  const openAsset = useOpenAsset();
  const dispatch = useAppDispatch();
  const api = useApiContext();
  const { user } = useUser();
  const queriedAssetsData = useAppSelector(selectQueriedAssets);
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);

  const [relatedAssetSetPickerData, setRelatedAssetSetPickerData] = useState<TpCollapsedRelationSet | null>(null); // If set, a modal is displayed to allow the user to pick a relation
  const [crossIconLocations, setCrossIconLocations] = useState({ x: 0, y: 0 });
  const { assetInfo, relations, setRelations } = props;
  const [lines, setLines] = useState([]);
  const [totalHeight, setTotalHeight] = useState(0);
  const [assetTags, setAssetTags] = useState([]);
  const [relationTags, setRelationTags] = useState([]);

  useEffect(() => {
    api.asset.listAssetRelations(assetInfo.id).then((relations) => {
      const updatedAssetInfo: TpAssetInfo = { ...assetInfo, relations: [] };
      for (const rel of relations) {
        if (rel.isReverse) {
          dispatch(addNewAsset({ asset: rel.toAsset }));
          updatedAssetInfo.relations.push({
            id: rel.id,
            to: rel.toAsset.id,
            type: rel.type,
          });
        } else {
          const asset: TpAssetInfo = { ...rel.toAsset, relations: [] };
          asset.relations.push({
            id: rel.id,
            to: assetInfo.id,
            type: rel.type,
          });
          if (queriedAssetsData.queriedAssets.find((qa) => qa.id == asset.id)) {
            dispatch(modifyAsset({ asset }));
          } else {
            dispatch(addNewAsset({ asset }));
          }
        }
      }
      dispatch(modifyOpenedAsset({ asset: updatedAssetInfo }));
    });
  }, [assetInfo.id]);

  useEffect(() => {
    const new_relations = getAssetRelations(assetInfo.id, queriedAssetsData);
    setRelations(new_relations);
  }, [assetInfo, queriedAssetsData]);

  const tagWidth = 220;
  const tagHeight = 45;
  const tagSepV = 15;

  function renderAssetTagContent(rel: TpCollapsedRelationSet, key: string, posX: number, posY: number) {
    let label = `${rel.relations.length} ${getAssetTypeName(ontology.assetTypes[rel.toAssetType], true)}`;
    if (rel.relations.length == 1) label = rel.relations[0].toAsset.displayName;
    return (
      <React.Fragment key={key}>
        {rel.relations.length > 1 && ( // We show a "stack" view to indicate that more than one asset is related
          <>
            <div
              className={styles.relatedAsset}
              style={{ left: `${posX + 14}px`, top: `${posY - tagHeight / 2 + 8}px`, opacity: 0.25 }}
            />
            <div
              className={styles.relatedAsset}
              style={{ left: `${posX + 7}px`, top: `${posY - tagHeight / 2 + 4}px`, opacity: 0.5 }}
            />
          </>
        )}
        <div
          className={styles.relatedAsset}
          style={{ left: `${posX}px`, top: `${posY - tagHeight / 2}px` }}
          onClick={() => {
            if (rel.relations.length == 1) openAsset(rel.relations[0].toAsset.id);
            else setRelatedAssetSetPickerData(rel);
          }}
        >
          <div className={styles.relatedAssetIcon}>
            <FontAwesomeIcon icon={['fas', getAssetTypeIcon(ontology.assetTypes[rel.toAssetType])]} />
          </div>
          <div className={styles.relatedAssetTypeName}>{getAssetTypeName(ontology.assetTypes[rel.toAssetType])}</div>
          <div className={styles.relatedAssetName}>{label}</div>
        </div>
      </React.Fragment>
    );
  }

  function renderCreateAssetTag(key: string, posX: number, posY: number, isReverse: boolean = false) {
    return (
      <React.Fragment key={key}>
        <div
          className={styles.createRelatedAsset}
          style={{ left: `${posX}px`, top: `${posY - tagHeight / 2}px` }}
          onClick={() => {
            promptAddRelationWizard(assetInfo, relations, ontology, api, isReverse, queriedAssetsData)
              .then((relation: TpAssetRelationInfo) => {
                setRelations([...relations, relation]);
              })
              .catch((error) => {
                if (error) {
                  console.error(error);
                  showErrorMessage(
                    'Error Creating new relationship for' +
                      assetInfo.displayName +
                      '. \n Please contact your administrator: \n ' +
                      error,
                  );
                }
                return;
              });
          }}
        >
          <div className={styles.relatedAssetIcon}>
            <FontAwesomeIcon icon={['fas', 'plus']} />
          </div>
          <div className={styles.createRelatedAssetName}>Add new Relation</div>
        </div>
      </React.Fragment>
    );
  }
  const onClickDelete = (relation: TpCollapsedRelationSet) => {
    promptDeleteRelation(relation, api).then((removedRelationId: string) => {
      setRelations(relations.filter((rel) => rel.id !== removedRelationId));
    });
  };

  useEffect(() => {
    const createDownRelations = (ontology.assetTypes[props.assetInfo.type].relations ?? []).filter(
      (r: any) => r.userCanCreate && user.canWrite,
    );
    const createUpRelations = Object.values(ontology.assetTypes).filter((assetType: any) => {
      return (
        assetType.relations &&
        assetType.relations.filter((r: any) => r.toAsset === assetInfo.type && r.userCanCreate && user.canWrite)
          .length > 0
      );
    });
    const relationsUp = collapseRelations(relations.filter((rel) => !rel.isReverse && rel.toAsset));
    const relationsDown = collapseRelations(relations.filter((rel) => rel.isReverse && rel.toAsset));

    const countUpRelations = createUpRelations.length > 0 ? relationsUp.length + 1 : relationsUp.length;
    const countDownRelations = createDownRelations.length > 0 ? relationsDown.length + 1 : relationsDown.length;

    const maxRelCount = Math.max(countUpRelations, countDownRelations, 1);

    const dynamicTotalHeight = maxRelCount * (tagHeight + tagSepV) - tagSepV + 20;
    const dynamicLines: any[] = [];
    const dynamicAssetTags: any[] = [];
    const DynamicRelationTags: any[] = [];

    // center tag = asset itself
    const centerTagXLeft = 380;
    const centerTagY = dynamicTotalHeight / 2 - 10;

    dynamicAssetTags.push(
      <div
        key="center"
        className={styles.centralAsset}
        style={{ left: `${centerTagXLeft}px`, top: `${centerTagY - tagHeight / 2}px` }}
      >
        {assetInfo.displayName}
      </div>,
    );

    const ofs1 = 50;
    const ofs2 = 70;
    const ofs3 = 120;

    const lineEndOffs = 4;

    let idx = 0;
    let lineStartX = centerTagXLeft + tagWidth;
    let xPos2 = 0;
    let xPos3 = 0;
    let yPos = 0;

    for (const rel of relationsDown) {
      xPos2 = lineStartX + ofs2;
      xPos3 = lineStartX + ofs3;
      yPos =
        tagHeight / 2 +
        idx * (tagHeight + tagSepV) +
        (Math.max(0, countUpRelations - countDownRelations) * (tagHeight + tagSepV)) / 2;
      dynamicAssetTags.push(renderAssetTagContent(rel, `assetD_${idx}`, xPos3, yPos));
      const relationOntology = ontology.assetTypes[assetInfo.type].relations.find((r: any) => r.identifier == rel.type);

      DynamicRelationTags.push(
        <div
          key={`relD_${idx}`}
          className={styles.relationTagDown}
          style={{ left: `${xPos3 - 10}px`, top: `${yPos + 5}px` }}
        >
          {getRelationTypeName(relationOntology, false)}
        </div>,
      );

      dynamicLines.push(
        <RelationLink
          key={`lineD_${idx}`}
          setCrossIconLocations={setCrossIconLocations}
          onClickDelete={onClickDelete}
          {...{ idx, lineStartX, centerTagY, ofs1, xPos2, xPos3, yPos, lineEndOffs, relation: rel, isReverse: true }}
        />,
      );

      idx++;
    }

    if (createDownRelations.length > 0) {
      xPos2 = lineStartX + ofs2;
      xPos3 = lineStartX + ofs3;
      yPos = yPos =
        tagHeight / 2 +
        idx * (tagHeight + tagSepV) +
        (Math.max(0, countUpRelations - countDownRelations) * (tagHeight + tagSepV)) / 2;
      dynamicAssetTags.push(renderCreateAssetTag(`assetD_${idx}`, xPos3, yPos, true));
      dynamicLines.push(
        <RelationLink
          key={`lineD_${idx}`}
          setCrossIconLocations={setCrossIconLocations}
          onClickDelete={onClickDelete}
          {...{ idx, lineStartX, centerTagY, ofs1, xPos2, xPos3, yPos, lineEndOffs, isReverse: true }}
        />,
      );
    }

    idx = 0;
    lineStartX = centerTagXLeft;
    for (const rel of relationsUp) {
      xPos2 = lineStartX - ofs2;
      xPos3 = lineStartX - ofs3;
      yPos =
        tagHeight / 2 +
        idx * (tagHeight + tagSepV) +
        (Math.max(0, countDownRelations - countUpRelations) * (tagHeight + tagSepV)) / 2;
      dynamicAssetTags.push(renderAssetTagContent(rel, `assetU_${idx}`, xPos3 - tagWidth, yPos));
      const relationOntology = ontology.assetTypes[rel.toAssetType].relations.find(
        (r: any) => r.identifier == rel.type,
      );

      DynamicRelationTags.push(
        <div
          key={`relU_${idx}`}
          className={styles.relationTagUp}
          style={{ left: `${xPos3 + 10}px`, top: `${yPos + 5}px` }}
        >
          {getRelationTypeName(relationOntology, true)}
        </div>,
      );

      dynamicLines.push(
        <RelationLink
          key={`lineU_${idx}`}
          setCrossIconLocations={setCrossIconLocations}
          onClickDelete={onClickDelete}
          {...{ idx, lineStartX, centerTagY, ofs1, xPos2, xPos3, yPos, lineEndOffs, relation: rel, isReverse: false }}
        />,
      );
      idx++;
    }
    if (createUpRelations.length > 0) {
      xPos2 = lineStartX - ofs2;
      xPos3 = lineStartX - ofs3;
      yPos =
        tagHeight / 2 +
        idx * (tagHeight + tagSepV) +
        (Math.max(0, countDownRelations - countUpRelations) * (tagHeight + tagSepV)) / 2;
      dynamicAssetTags.push(renderCreateAssetTag(`assetU_${idx}`, xPos3 - tagWidth, yPos));
      dynamicLines.push(
        <RelationLink
          key={`lineU_${idx}`}
          setCrossIconLocations={setCrossIconLocations}
          onClickDelete={onClickDelete}
          {...{ idx, lineStartX, centerTagY, ofs1, xPos2, xPos3, yPos, lineEndOffs, isReverse: false }}
        />,
      );
    }
    setTotalHeight(dynamicTotalHeight);
    setLines(dynamicLines);
    setAssetTags(dynamicAssetTags);
    setRelationTags(DynamicRelationTags);
  }, [assetInfo, relations]);

  return (
    <>
      <div style={{ height: '20px' }} />
      <div className={styles.relationsView} style={{ position: 'relative', height: `${totalHeight}px` }}>
        <svg
          style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: `${totalHeight}px`, fontSize: '15px' }}
        >
          <g>{lines}</g>
        </svg>
        {relationTags}
        {assetTags}
        {crossIconLocations.x > 0 && crossIconLocations.y > 0 && (
          <FontAwesomeIcon
            icon={['fas', 'times']}
            style={{
              position: 'absolute',
              left: crossIconLocations.x,
              top: crossIconLocations.y,
              fontSize: '20px',
              color: 'red',
              cursor: 'pointer',
              zIndex: 100,
            }}
          />
        )}
      </div>
      {relatedAssetSetPickerData && (
        <ModalPortal close={() => setRelatedAssetSetPickerData(null)}>
          <RelatedAssetsPopup relatedAssetInfo={relatedAssetSetPickerData} />
        </ModalPortal>
      )}
    </>
  );
};

export default Relations;
